import { Checkbox } from '@perch-labs/shrike';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import '@reach/dialog/styles.css';
import { Button } from '~/components/button';
import { Container } from '~/components/layout';
import { TermsText } from '~/components/terms/terms-text';
import { Description, Title } from '~/components/typography';
import { useDialog } from '~/hooks';
import { createPersonalizedContractPreview } from '~/services/create-personalized-contract-preview';
import { ContractVersion } from '~/typings/context';
import { EventName } from '~/utils/logger';
import { sortContracts } from '~/utils/sort-contracts';
import { trackError } from '~/utils/tracking';
import { ScrollablePdf } from './scrollable-pdf.component';
import {
  Body,
  ButtonContainer,
  CloseButton,
  CloseIcon,
  Content,
  Main,
  Overlay,
} from './scrollable-terms-dialog.style';

type ContractType = 'disclosure' | 'data_consent' | 'direct';

type ContractPerchFields = ContractVersion & {
  base64PerchPersonalizedFile?: string | null;
  base64PerchPersonalizedFiles?: Array<{
    utilityAccountId?: number | null;
    base64PerchPersonalizedFile?: string | null;
  }> | null;
};

function normalizePdfDataUrl(base64: string): string {
  return base64.startsWith('data:') ? base64 : `data:application/pdf;base64,${base64}`;
}

/** All Perch PDF URLs for scroll (usage_request batch or single field / file URL). */
export function getScrollablePerchPdfItems(
  contract: ContractVersion,
): { key: string; pdfUrl: string }[] {
  const ext = contract as ContractPerchFields;
  if (ext.base64PerchPersonalizedFiles?.some(f => f.base64PerchPersonalizedFile)) {
    return ext
      .base64PerchPersonalizedFiles!.filter(f => f.base64PerchPersonalizedFile)
      .map((f, i) => ({
        key: `${contract.id}-ua-${f.utilityAccountId ?? i}`,
        pdfUrl: normalizePdfDataUrl(f.base64PerchPersonalizedFile as string),
      }));
  }
  if (ext.base64PerchPersonalizedFile) {
    return [
      {
        key: `${contract.id}-perch-single`,
        pdfUrl: normalizePdfDataUrl(ext.base64PerchPersonalizedFile),
      },
    ];
  }
  if (contract.fileUrl) {
    return [{ key: `${contract.id}-file`, pdfUrl: contract.fileUrl }];
  }
  return [];
}

export interface ScrollableTermsDialogProps {
  onSubmit: () => void;
  pending: boolean;
  contracts: ContractVersion[];
  showPersonalizedContractPreview?: boolean;
  utilityAccountId?: number;
  consolidateTermsText?: boolean;
  fetchPersonalizedContractForContractType?: Array<ContractType>;
  tabsData?: {
    houseOccupancy?: string;
    lmiProgram?: string[];
    county?: string;
    houseIncome?: string;
    lmiSourceSelection?: string[];
  };
}

export const ScrollableTermsDialog = ({
  children,
  contracts,
  onSubmit,
  pending,
  utilityAccountId,
  consolidateTermsText = true,
  fetchPersonalizedContractForContractType,
  showPersonalizedContractPreview = false,
  tabsData,
}: PropsWithChildren<ScrollableTermsDialogProps>) => {
  const [checked, setChecked] = useState<boolean[]>(contracts.map(() => false));
  const [checkedAll, setCheckedAll] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const { isOpen, close, TargetComponent } = useDialog(<span>{children}</span>);
  const scrollableContracts = sortContracts(contracts.filter(c => c.scrollRequired));
  const nonScrollableContracts = contracts
    .filter(c => !c.scrollRequired)
    .sort((a, b) => a.id - b.id);

  /** Use FetchPersonalizedSignupContractSetFromTemplate result when present (e.g. Perch NJ) to avoid calling CreatePersonalizedContractPreview again. */
  const initialModifiedContracts = scrollableContracts.map(c => {
    const ext = c as ContractPerchFields;
    if (ext.base64PerchPersonalizedFiles?.some(f => f.base64PerchPersonalizedFile)) {
      const first = ext.base64PerchPersonalizedFiles?.find(
        f => f.base64PerchPersonalizedFile,
      )?.base64PerchPersonalizedFile;
      if (first) {
        const pdfUrl = first.startsWith('data:') ? first : `data:application/pdf;base64,${first}`;
        return { ...c, fileUrl: pdfUrl };
      }
    }
    const base64 = ext.base64PerchPersonalizedFile;
    if (base64) {
      const pdfUrl = base64.startsWith('data:') ? base64 : `data:application/pdf;base64,${base64}`;
      return { ...c, fileUrl: pdfUrl };
    }
    return c;
  });
  const [modifiedContracts, setModifiedContracts] =
    useState<ContractVersion[]>(initialModifiedContracts);

  const pdfRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const fetchContracts = async () => {
      if (showPersonalizedContractPreview && utilityAccountId) {
        for (const contract of scrollableContracts) {
          const filterContractType =
            !fetchPersonalizedContractForContractType?.length ||
            fetchPersonalizedContractForContractType.includes(
              contract.contractType as ContractType,
            );
          const shouldPersonalize = contract.hasPersonalization === true && filterContractType;
          const cExt = contract as {
            base64PerchPersonalizedFile?: string | null;
            base64PerchPersonalizedFiles?: { base64PerchPersonalizedFile?: string | null }[] | null;
          };
          const hasPerchPreview =
            !!cExt.base64PerchPersonalizedFile ||
            !!cExt.base64PerchPersonalizedFiles?.some(f => f.base64PerchPersonalizedFile);
          if (shouldPersonalize && !hasPerchPreview) {
            try {
              const response = await createPersonalizedContractPreview({
                contractVersionId: contract.id,
                utilityAccountId,
                tabsData: tabsData || {},
              });

              const pdfUrl = response.base64.startsWith('data:')
                ? response.base64
                : `data:application/pdf;base64,${response.base64}`;

              setModifiedContracts(prev =>
                prev.map(c => (c.id === contract.id ? { ...c, fileUrl: pdfUrl } : c)),
              );
            } catch (e) {
              trackError(new Error('Error creating personalized contract preview'), {
                extras: {
                  error: e,
                  contractId: contract.id,
                },
              });
            }
          }
        }
      }
    };

    fetchContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = e => {
    const { offsetHeight, scrollHeight, scrollTop } = e.target;
    if (offsetHeight + scrollTop >= scrollHeight) {
      setScrolledToBottom(true);
    } else if (scrolledToBottom) {
      // toggle back to starting state if user scrolls up again after scrolling down
      setScrolledToBottom(false);
    }
  };

  const scrollToBottom = () => {
    const { current } = pdfRef;
    if (!current) return;
    current.scrollTop = current.scrollHeight - current.clientHeight;
    setScrolledToBottom(true);
  };

  const isAllTermsAccepted = () => {
    if (consolidateTermsText) {
      return checkedAll;
    } else {
      return checked.every(c => c);
    }
  };
  return (
    <>
      {TargetComponent}
      <Overlay
        isOpen={isOpen}
        onDismiss={_event => {
          setScrolledToBottom(false);
          close();
        }}
        data-testid="terms-dialog-overlay"
      >
        <Body aria-label="Community solar terms and conditions">
          <ButtonContainer>
            <CloseButton
              data-testid="terms-dialog-close"
              onClick={_event => {
                setScrolledToBottom(false);
                close();
              }}
            >
              <CloseIcon />
            </CloseButton>
          </ButtonContainer>
          <Content height="100%" padding={0}>
            <Main onScroll={handleScroll} ref={pdfRef}>
              {(() => {
                const totalPdfs = modifiedContracts.reduce(
                  (n, c) => n + getScrollablePerchPdfItems(c).length,
                  0,
                );
                let cumulativePdfIndex = 0;
                return modifiedContracts.map((contract, contractIndex) => {
                  const pdfItems = getScrollablePerchPdfItems(contract);
                  const baseIndex = cumulativePdfIndex;
                  cumulativePdfIndex += pdfItems.length;
                  return (
                    <div key={contract.id}>
                      {modifiedContracts.length === 1 && (
                        <Title>{`Agree to the ${
                          contract.displayName ?? 'following contract'
                        }`}</Title>
                      )}
                      {contractIndex === 0 && (
                        <Description mb={4}>
                          This agreement says you will support a community solar farm and receive
                          savings on your utility bill. You can cancel for any reason. Please scroll
                          through and check the box to participate.
                        </Description>
                      )}
                      {pdfItems.map((item, pdfIndex) => {
                        const globalPdfIndex = baseIndex + pdfIndex;
                        return (
                          <span key={item.key}>
                            <ScrollablePdf pdfUrl={item.pdfUrl} />
                            {globalPdfIndex < totalPdfs - 1 ? <hr /> : null}
                          </span>
                        );
                      })}
                    </div>
                  );
                });
              })()}
              {getTermsSection(consolidateTermsText)}
            </Main>
            <Container margin={4}>
              <Button
                mode={!scrolledToBottom && !isAllTermsAccepted() ? 'default' : 'primary'}
                disabled={!!scrolledToBottom && !isAllTermsAccepted()}
                loading={pending}
                onClick={() => {
                  if (isAllTermsAccepted()) {
                    onSubmit();
                  } else {
                    scrollToBottom();
                    if (consolidateTermsText) {
                      setCheckedAll(true);
                    }
                  }
                }}
                testId="scroll_agree"
              >
                {getSubmitButtonText()}
              </Button>
            </Container>
          </Content>
        </Body>
      </Overlay>
    </>
  );
  function getSubmitButtonText() {
    if (!isAllTermsAccepted() && !scrolledToBottom) {
      return 'Click to scroll & agree';
    }
    if (modifiedContracts.length === 1) {
      return 'Submit agreement';
    }
    return 'Submit agreements';
  }

  function getTermsSection(consolidateTermsText: boolean) {
    if (consolidateTermsText) {
      return (
        <Checkbox
          checked={checkedAll}
          margin="0 0 16px 0"
          onChange={e => {
            const value = e.target.checked;
            // stops the screen from scrolling strangely when you click the checkbox
            const { current } = pdfRef;
            if (!current) return;
            current.scrollIntoView(true);
            setCheckedAll(value);
          }}
        >
          {/* Only ever rendered from SolarTerms on the legal screen. (SUBEXP-3193) */}
          <TermsText
            contracts={[...scrollableContracts, ...nonScrollableContracts]}
            lastConjunction=" and the "
            preText="I have read the"
            postText=". By proceeding, I will be submitting my e-signature."
            utilityAccountId={utilityAccountId}
            showPersonalizedContractPreview={showPersonalizedContractPreview}
            screen={EventName.LegalScreen}
          />
        </Checkbox>
      );
    } else {
      return (
        <>
          {[...scrollableContracts, ...nonScrollableContracts].map((contract, index) => (
            <Checkbox
              key={contract.id}
              checked={checked[index]}
              margin="0 0 16px 0"
              onChange={e => {
                const value = e.target.checked;
                // stops the screen from scrolling strangely when you click the checkbox
                const { current } = pdfRef;
                if (!current) return;
                current.scrollIntoView(true);
                setChecked(prev => {
                  const newChecked = [...prev];
                  newChecked[index] = value;
                  return newChecked;
                });
              }}
            >
              <TermsText
                key={contract.id}
                contracts={[contract]}
                lastConjunction=" and the "
                preText="I have read the"
                postText=". By proceeding, I will be submitting my e-signature."
                utilityAccountId={utilityAccountId}
                showPersonalizedContractPreview
                screen={EventName.LegalScreen}
              />
            </Checkbox>
          ))}
        </>
      );
    }
  }
};
